import { applyFieldFormula, formatReportValue } from './reportFormat';
import {
  colLabel,
  isHiddenReportColumn,
  isResultColumnKey,
  isSqlColumnKey,
  resolveColumnKey,
  withPersianLabel,
} from './columnLabels';

export const DISPLAY_TYPES = [
  { value: 'text', label: 'متن' },
  { value: 'bar', label: 'نوار درصد' },
  { value: 'gauge', label: 'گیج' },
  { value: 'ring', label: 'حلقه' },
  { value: 'badge', label: 'نشان درصد' },
  { value: 'emoji', label: 'ایموجی' },
  { value: 'stars', label: 'ستاره' },
];

export const METER_DISPLAYS = ['bar', 'gauge', 'ring', 'badge'];

export function isMeterDisplay(display) {
  return METER_DISPLAYS.includes(display);
}

export const EMOJI_PRESETS = {
  traffic: [
    { min: 80, emoji: '🟢' },
    { min: 50, emoji: '🟡' },
    { min: 0, emoji: '🔴' },
  ],
  smile: [
    { min: 80, emoji: '😄' },
    { min: 50, emoji: '😐' },
    { min: 0, emoji: '😞' },
  ],
  check: [
    { min: 80, emoji: '✅' },
    { min: 50, emoji: '➖' },
    { min: 0, emoji: '❌' },
  ],
};

export const EMOJI_PRESET_OPTIONS = [
  { value: 'traffic', label: 'چراغ راهنما' },
  { value: 'smile', label: 'چهره' },
  { value: 'check', label: 'تیک / ضربدر' },
  { value: 'custom', label: 'سفارشی' },
];

const TEMPLATE_KEY_RE = /\{([a-zA-Z0-9_]+)\}/g;
const ANY_BRACE_RE = /\{([^}]+)\}/g;
const TRAILING_BRACE_RE = /\{([^{}\n]*)$/;
const TRAILING_IDENT_RE = /([A-Za-z_][A-Za-z0-9_]*)$/;
const TRAILING_WORD_RE = /([^\s{}()+\-*/×÷]+)$/;

/**
 * Filter column keys by English name or Persian label.
 * Picked keys stay in the list so the same column can be inserted again.
 */
export function filterColumnSuggestions(options, query, labelFn) {
  const list = (options || []).filter((key) => key && isResultColumnKey(key) && !isHiddenReportColumn(key));
  const q = String(query || '').trim().toLowerCase();
  if (!q) return list;
  const getLabel = typeof labelFn === 'function' ? labelFn : (k) => k;
  return list.filter((key) => {
    const k = String(key).toLowerCase();
    const label = String(getLabel(key) || '').toLowerCase();
    return k.includes(q) || label.includes(q);
  });
}

/**
 * Trailing `{ident` or bare identifier at the caret, used to filter suggestions.
 */
export function trailingColumnQuery(text, cursor) {
  const s = String(text || '');
  const pos = cursor == null ? s.length : Math.max(0, Math.min(s.length, Number(cursor) || 0));
  const before = s.slice(0, pos);
  const brace = before.match(TRAILING_BRACE_RE);
  if (brace) {
    return { query: brace[1], start: pos - brace[0].length, end: pos, braced: true };
  }
  const ident = before.match(TRAILING_IDENT_RE);
  if (ident) {
    return { query: ident[1], start: pos - ident[0].length, end: pos, braced: false };
  }
  const word = before.match(TRAILING_WORD_RE);
  if (word) {
    return { query: word[1], start: pos - word[0].length, end: pos, braced: false };
  }
  return { query: '', start: pos, end: pos, braced: false };
}

/**
 * Replace the trailing query (or insert at the caret) with `{token}`.
 * `tokenText` is the UI alias (Persian label); `key` is the fallback English name.
 * Never consumes the key from the suggestion list.
 */
export function insertColumnToken(text, key, cursor, tokenText) {
  const s = String(text || '');
  const pos = cursor == null ? s.length : Math.max(0, Math.min(s.length, Number(cursor) || 0));
  const trail = trailingColumnQuery(s, pos);
  let token = `{${tokenText || key}}`;
  if (!trail.query && trail.start > 0) {
    const prev = s[trail.start - 1];
    if (prev && !/[\s(+*/-]/.test(prev)) token = ` ${token}`;
  }
  return {
    text: s.slice(0, trail.start) + token + s.slice(trail.end),
    cursor: trail.start + token.length,
  };
}

/** Stored `{english_key}` → UI `{persian alias}`. */
export function mathToDisplay(expr, knownKeys = [], labelFn = colLabel) {
  return String(expr || '').replace(ANY_BRACE_RE, (_, inner) => {
    const key = resolveColumnKey(inner, knownKeys, labelFn);
    return `{${labelFn(key) || key}}`;
  });
}

/** UI `{persian alias}` → stored `{english_key}`. */
export function displayToMath(text, knownKeys = [], labelFn = colLabel) {
  return String(text || '').replace(ANY_BRACE_RE, (_, inner) => {
    const key = resolveColumnKey(inner, knownKeys, labelFn);
    return key ? `{${key}}` : '0';
  });
}

function sqlStringLiteral(value) {
  return `'${String(value).replace(/'/g, "''")}'`;
}

function sqlNullZero(expr) {
  const e = String(expr || '').trim();
  if (!e || e === '0') return '0';
  if (/^coalesce\s*\(/i.test(e)) return e;
  return `COALESCE(${e}, 0)`;
}

/**
 * Compile a stored math expression to a parenthesized SQL select expr.
 * `{persian alias}` and `{english_key}` both resolve. Missing/NULL → 0.
 * `qualify(key)` returns `a.net_pack_qty` or `SUM(a.net_pack_qty)`.
 */
function sqlColumnRef(key, qualify) {
  const qfn = typeof qualify === 'function' ? qualify : (k) => k;
  const raw = qfn(key);
  if (raw && raw !== key) return raw;
  if (isSqlColumnKey(key)) return raw || key;
  return `"${String(key).replace(/"/g, '""')}"`;
}

export function compileMathToSql(expr, qualify, knownKeys = []) {
  let s = String(expr || '').trim();
  if (!s) return '';
  s = s.replace(/×/g, '*').replace(/÷/g, '/');
  const placeholders = [];
  const mark = (key) => {
    placeholders.push(key);
    return `__COL${placeholders.length - 1}__`;
  };
  s = s.replace(ANY_BRACE_RE, (_, inner) => {
    const key = resolveColumnKey(inner, knownKeys);
    return key ? mark(key) : '0';
  });
  const keys = [...new Set([...(knownKeys || []), ...mathFieldKeys(expr, knownKeys)])]
    .filter((k) => isResultColumnKey(k))
    .sort((a, b) => b.length - a.length);
  keys.forEach((key) => {
    const re = isSqlColumnKey(key)
      ? new RegExp(`(^|[^A-Za-z0-9_])${escapeRegExp(key)}(?![A-Za-z0-9_])`, 'g')
      : new RegExp(`(^|[^\\p{L}\\p{N}_])${escapeRegExp(key)}(?![\\p{L}\\p{N}_])`, 'gu');
    s = s.replace(re, (_, pre) => `${pre}${mark(key)}`);
  });
  s = s.replace(/__COL(\d+)__/g, (_, idx) => sqlNullZero(sqlColumnRef(placeholders[Number(idx)], qualify)));
  if (!/^[\p{L}\p{N}_.,\s+\-*/()"']+$/u.test(s)) return '';
  return `(${s})`;
}

/** Compile a `{col}` text template to Postgres `||` concatenation. */
export function compileTemplateToSql(template, qualify) {
  const s = String(template || '');
  if (!s) return '';
  const qfn = typeof qualify === 'function' ? qualify : (k) => k;
  const parts = [];
  let last = 0;
  s.replace(ANY_BRACE_RE, (m, inner, idx) => {
    if (idx > last) parts.push(sqlStringLiteral(s.slice(last, idx)));
    const key = resolveColumnKey(inner);
    parts.push(`COALESCE((${qfn(key) || key})::text, '')`);
    last = idx + m.length;
    return m;
  });
  if (last < s.length) parts.push(sqlStringLiteral(s.slice(last)));
  if (!parts.length) return '';
  return `(${parts.join(' || ')})`;
}

export function sqlIdent(name) {
  const s = String(name || '').replace(/[^a-zA-Z0-9_]/g, '');
  if (!s) return '';
  return /^[0-9]/.test(s) ? `c_${s}` : s;
}

export function interpolateTemplate(template, row) {
  if (template == null || template === '') return '';
  return String(template).replace(TEMPLATE_KEY_RE, (_, key) => {
    const v = row?.[key];
    if (v == null || v === '') return '';
    return String(v);
  });
}

export function templateFieldKeys(template) {
  const keys = [];
  String(template || '').replace(TEMPLATE_KEY_RE, (_, key) => {
    if (key && !keys.includes(key)) keys.push(key);
    return '';
  });
  return keys;
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function numToken(v) {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '0';
}

/** Identifiers used in a column-math expression (`{net_pack_qty}` or `{کارتن خالص}`). */
export function mathFieldKeys(expr, knownKeys = []) {
  const found = [];
  String(expr || '').replace(ANY_BRACE_RE, (_, inner) => {
    const key = resolveColumnKey(inner, knownKeys);
    if (key && !found.includes(key)) found.push(key);
    return '';
  });
  const known = (knownKeys || []).filter((k) => isResultColumnKey(k)).sort((a, b) => b.length - a.length);
  const rest = String(expr || '').replace(ANY_BRACE_RE, ' ');
  known.forEach((key) => {
    const re = isSqlColumnKey(key)
      ? new RegExp(`(^|[^A-Za-z0-9_])${escapeRegExp(key)}(?![A-Za-z0-9_])`)
      : new RegExp(`(^|[^\\p{L}\\p{N}_])${escapeRegExp(key)}(?![\\p{L}\\p{N}_])`, 'u');
    if (re.test(rest) && !found.includes(key)) found.push(key);
  });
  return found;
}

/**
 * Safe arithmetic over query columns. Supports + - * / ( ) and `{col}` or bare names.
 * Anything other than numbers and operators is rejected.
 */
export function evaluateColumnMath(expr, row = {}, knownKeys = []) {
  let s = String(expr || '').trim();
  if (!s) return undefined;
  s = s.replace(/×/g, '*').replace(/÷/g, '/');
  s = s.replace(ANY_BRACE_RE, (_, inner) => {
    const key = resolveColumnKey(inner, knownKeys);
    return numToken(key ? row[key] : undefined);
  });
  const keys = [...new Set([...(knownKeys || []), ...Object.keys(row || {})])]
    .filter((k) => isResultColumnKey(k))
    .sort((a, b) => b.length - a.length);
  keys.forEach((key) => {
    const re = isSqlColumnKey(key)
      ? new RegExp(`(^|[^A-Za-z0-9_])${escapeRegExp(key)}(?![A-Za-z0-9_])`, 'g')
      : new RegExp(`(^|[^\\p{L}\\p{N}_])${escapeRegExp(key)}(?![\\p{L}\\p{N}_])`, 'gu');
    s = s.replace(re, (_, pre) => `${pre}${numToken(row[key])}`);
  });
  if (!/^[\d.,\s+\-*/().]+$/.test(s)) return undefined;
  try {
    // eslint-disable-next-line no-new-func
    const out = Function(`"use strict"; return (${s});`)();
    return typeof out === 'number' && Number.isFinite(out) ? out : undefined;
  } catch {
    return undefined;
  }
}

export function looksLikeColumnMath(expr) {
  const s = String(expr || '').trim();
  if (!s) return false;
  if (/\b(row|value|Math|Number|String|Date)\b/.test(s)) return false;
  if (/[;`]|process|require|window|document|Function|eval/i.test(s)) return false;
  return /[+\-*/]/.test(s);
}

export function isCustomColumn(col) {
  return col?.source === 'custom' || /^custom_\d+$/.test(String(col?.key || ''));
}

export function nextCustomColumnKey(columns = []) {
  let max = 0;
  columns.forEach((c) => {
    const m = String(c?.key || '').match(/^custom_(\d+)$/);
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `custom_${max + 1}`;
}

export function resolveColumnValue(row, col = {}, knownKeys) {
  const keys = knownKeys || Object.keys(row || {});
  if (col.math) {
    const computed = evaluateColumnMath(col.math, row, keys);
    if (computed !== undefined) return applyFieldFormula(computed, col.formula, row);
    if (row && Object.prototype.hasOwnProperty.call(row, col.key)) {
      return applyFieldFormula(row[col.key], col.formula, row);
    }
    return applyFieldFormula(undefined, col.formula, row);
  }
  if (looksLikeColumnMath(col.formula)) {
    const computed = evaluateColumnMath(col.formula, row, keys);
    if (computed !== undefined) return computed;
  }
  const fromTemplate = col.template
    ? interpolateTemplate(col.template, row)
    : undefined;
  const raw = fromTemplate !== undefined
    ? fromTemplate
    : (row ? row[col.key] : undefined);
  return applyFieldFormula(raw, col.formula, row);
}

export function matchConditionalRule(value, rules = []) {
  if (!rules?.length) return null;
  const num = Number(value);
  const comparable = Number.isFinite(num);
  for (const rule of rules) {
    const ruleVal = Number(rule.value);
    let ok = false;
    if (rule.op === '>=') ok = comparable && num >= ruleVal;
    else if (rule.op === '<=') ok = comparable && num <= ruleVal;
    else if (rule.op === '>') ok = comparable && num > ruleVal;
    else if (rule.op === '<') ok = comparable && num < ruleVal;
    else if (rule.op === '=') {
      if (comparable && Number.isFinite(ruleVal)) ok = num === ruleVal;
      else ok = String(value ?? '') === String(rule.value ?? '');
    }
    if (ok) return rule;
  }
  return null;
}

function compareValueForRules(row, col, value) {
  if (isMeterDisplay(col?.display)) {
    const bar = resolveBarDisplay(row, col, value);
    return bar.hasGoal ? bar.percent : undefined;
  }
  return value;
}

export function resolveRowStyle(row, cols = []) {
  for (const col of cols) {
    const val = resolveColumnValue(row, col);
    const compare = compareValueForRules(row, col, val);
    const rule = matchConditionalRule(compare, col.conditionalFormat);
    if (rule?.apply === 'row') {
      const style = {};
      if (rule.bg) style.backgroundColor = rule.bg;
      if (rule.color) style.color = rule.color;
      return { style, className: rule.className || '', meter: isMeterDisplay(col.display) ? resolveBarDisplay(row, col, val) : null };
    }
  }
  const meterCol = (cols || []).find((c) => isMeterDisplay(c?.display) && c.barFillRow);
  if (meterCol) {
    const val = resolveColumnValue(row, meterCol);
    return { style: {}, className: '', meter: resolveBarDisplay(row, meterCol, val) };
  }
  return { style: {}, className: '', meter: null };
}

export function resolveCellStyle(col, value, row) {
  const compare = compareValueForRules(row, col, value);
  const rule = matchConditionalRule(compare, col?.conditionalFormat);
  const style = {};
  if (col?.bgColor) style.backgroundColor = col.bgColor;
  if (col?.fontColor) style.color = col.fontColor;
  if (rule && rule.apply !== 'row' && rule.apply !== 'bar') {
    if (rule.bg) style.backgroundColor = rule.bg;
    if (rule.color) style.color = rule.color;
  }
  return {
    style,
    className: (rule && rule.apply !== 'row' && rule.apply !== 'bar' && rule.className) ? rule.className : '',
    dir: col?.dir || undefined,
  };
}

export function emojiRulesFor(col = {}) {
  if (col.emojiPreset === 'custom' && Array.isArray(col.emojiRules) && col.emojiRules.length) {
    return col.emojiRules;
  }
  if (col.emojiPreset && EMOJI_PRESETS[col.emojiPreset]) {
    return EMOJI_PRESETS[col.emojiPreset];
  }
  if (Array.isArray(col.emojiRules) && col.emojiRules.length) return col.emojiRules;
  return EMOJI_PRESETS.traffic;
}

export function pickEmoji(value, col) {
  const rules = [...emojiRulesFor(col)].sort((a, b) => Number(b.min) - Number(a.min));
  const num = Number(value);
  for (const r of rules) {
    if (Number.isFinite(num) && num >= Number(r.min)) return r.emoji || '';
  }
  return rules[rules.length - 1]?.emoji || '';
}

export const TEXT_SIZE_OPTIONS = [
  { value: 'xs', label: '۱۰', hint: 'خیلی کوچک', className: 'text-[10px]' },
  { value: 'sm', label: '۱۱', hint: 'کوچک', className: 'text-[11px]' },
  { value: 'md', label: '۱۲', hint: 'متوسط', className: 'text-xs' },
  { value: 'lg', label: '۱۴', hint: 'بزرگ', className: 'text-sm' },
  { value: 'xl', label: '۱۶', hint: 'خیلی بزرگ', className: 'text-base' },
];

const TEXT_SIZE_CLASS = Object.fromEntries(TEXT_SIZE_OPTIONS.map((o) => [o.value, o.className]));

export const HEADER_ALIGN_OPTIONS = [
  { value: 'right', label: 'راست' },
  { value: 'center', label: 'وسط' },
  { value: 'left', label: 'چپ' },
];

export const BAR_MARKER_OPTIONS = [
  { value: 'none', label: 'بدون نشانگر' },
  { value: 'arrow', label: 'فلش' },
  { value: 'line', label: 'خط' },
  { value: 'dot', label: 'نقطه' },
];

export const BAR_SIZE_OPTIONS = [
  { value: 'xs', label: '۱۰', hint: 'خیلی کوچک', track: 6, gauge: 52, labelClass: 'text-[9px]' },
  { value: 'sm', label: '۱۱', hint: 'کوچک', track: 8, gauge: 64, labelClass: 'text-[10px]' },
  { value: 'md', label: '۱۲', hint: 'متوسط', track: 10, gauge: 76, labelClass: 'text-[11px]' },
  { value: 'lg', label: '۱۴', hint: 'بزرگ', track: 14, gauge: 92, labelClass: 'text-xs' },
  { value: 'xl', label: '۱۶', hint: 'خیلی بزرگ', track: 18, gauge: 108, labelClass: 'text-sm' },
];

const BAR_SIZE = Object.fromEntries(BAR_SIZE_OPTIONS.map((o) => [o.value, o]));

export function resolveBarSize(col = {}, tableLook = resolveTableLook()) {
  const key = BAR_SIZE[col.barSize] ? col.barSize
    : (BAR_SIZE[col.textSize] ? col.textSize : (BAR_SIZE[tableLook.textSize] ? tableLook.textSize : 'sm'));
  return BAR_SIZE[key] || BAR_SIZE.sm;
}

export function resolveTextSizeClass(size, fallback = 'sm') {
  if (TEXT_SIZE_CLASS[size]) return TEXT_SIZE_CLASS[size];
  return TEXT_SIZE_CLASS[fallback] || 'text-[11px]';
}

export function resolveTableLook(tableConfig = {}) {
  const textSize = TEXT_SIZE_CLASS[tableConfig.textSize] ? tableConfig.textSize : 'sm';
  const headerTextSize = TEXT_SIZE_CLASS[tableConfig.headerTextSize]
    ? tableConfig.headerTextSize
    : textSize;
  const headerAlign = HEADER_ALIGN_OPTIONS.some((o) => o.value === tableConfig.headerAlign)
    ? tableConfig.headerAlign
    : 'right';
  return {
    textSize,
    headerTextSize,
    headerAlign,
    headerBold: tableConfig.headerBold !== false,
    headerItalic: !!tableConfig.headerItalic,
    headerColor: tableConfig.headerColor || '',
    headerBg: tableConfig.headerBg || '',
    compact: !!tableConfig.compact,
    textClass: resolveTextSizeClass(textSize),
    headerTextClass: resolveTextSizeClass(headerTextSize),
  };
}

export function resolveColumnTextClass(col = {}, tableLook = resolveTableLook()) {
  return resolveTextSizeClass(col.textSize, tableLook.textSize);
}

function finiteNumber(value) {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const s = String(value).replace(/[٪%,\s]/g, '').replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Sale/target meter: start and end can be other query fields.
 * Progress only when a goal exists (end field or an explicit fixed cap).
 * Display percent is unclamped so 120% or −۱۰٪ still show.
 */
export function resolveBarDisplay(row, col = {}, value, tableLook) {
  const startKey = col.barStartKey || col.barValueKey || '';
  const endKey = col.barEndKey || col.barMaxKey || '';
  const startRaw = (startKey && row && row[startKey] != null && row[startKey] !== '')
    ? row[startKey]
    : value;
  const start = finiteNumber(startRaw);
  const endFromKey = endKey && row ? finiteNumber(row[endKey]) : undefined;
  const endFromMax = !endKey ? finiteNumber(col.barMax) : undefined;
  const hasGoal = (endFromKey != null && endFromKey > 0)
    || (endFromMax != null && endFromMax > 0);
  const end = hasGoal
    ? ((endFromKey != null && endFromKey > 0) ? endFromKey : endFromMax)
    : 0;
  const n = start != null ? start : 0;
  const rawPercent = hasGoal && end > 0 ? (n / end) * 100 : null;
  const percent = rawPercent;
  const fillPercent = rawPercent == null ? 0 : Math.max(0, Math.min(100, rawPercent));
  const barRules = (col.conditionalFormat || []).filter((r) => (r.apply || 'cell') !== 'row');
  const rule = hasGoal ? matchConditionalRule(percent, barRules) : null;
  const marker = BAR_MARKER_OPTIONS.some((o) => o.value === col.barMarker) ? col.barMarker : 'none';
  const fill = rule?.color || col.barColor || col.fontColor || '#6366f1';
  const track = rule?.bg || col.barTrackColor || 'rgba(15, 23, 42, 0.08)';
  return {
    percent,
    fillPercent,
    hasGoal,
    start: n,
    end,
    startKey,
    endKey,
    startRaw,
    endRaw: endFromKey != null ? row?.[endKey] : (hasGoal ? end : undefined),
    fill,
    track,
    marker,
    border: !!col.barBorder,
    borderColor: col.barBorderColor || fill,
    showLabels: col.barShowLabels !== false,
    showPercent: col.barShowPercent !== false && hasGoal,
    fillRow: !!col.barFillRow && hasGoal,
    size: resolveBarSize(col, tableLook),
  };
}

export function barPercent(value, col, row) {
  return resolveBarDisplay(row, col, value).percent;
}

export function starCount(value, col) {
  const max = Number(col?.barMax);
  const cap = Number.isFinite(max) && max > 0 ? max : 100;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, Math.round((n / cap) * 5)));
}

export function formatColumnExport(row, col) {
  const value = resolveColumnValue(row, col);
  const display = col?.display || 'text';
  if (display === 'emoji') return pickEmoji(value, col);
  if (display === 'stars') {
    const n = starCount(value, col);
    return `${'★'.repeat(n)}${'☆'.repeat(5 - n)}`;
  }
  if (isMeterDisplay(display)) {
    const p = barPercent(value, col, row);
    return p == null ? '' : `${Math.round(p)}%`;
  }
  return formatReportValue(value, col);
}

/** Value for spreadsheet cells: keep numbers numeric when possible. */
export function excelColumnValue(row, col) {
  const value = resolveColumnValue(row, col);
  const display = col?.display || 'text';
  if (display === 'emoji') return pickEmoji(value, col);
  if (display === 'stars') {
    const n = starCount(value, col);
    return `${'★'.repeat(n)}${'☆'.repeat(5 - n)}`;
  }
  if (isMeterDisplay(display)) {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

export function defaultTableColumn() {
  return {
    key: '',
    label: '',
    source: 'field',
    display: 'text',
    dir: '',
    format: 'number',
    decimals: 2,
    useGrouping: true,
    currencySuffix: 'none',
    align: 'right',
    bold: false,
    italic: false,
    fontColor: '',
    bgColor: '',
    formula: '',
    template: '',
    math: '',
    compute: '',
    barMax: '',
    barStartKey: '',
    barEndKey: '',
    barMarker: 'none',
    barBorder: false,
    barBorderColor: '',
    barColor: '',
    barTrackColor: '',
    barSize: '',
    barShowLabels: true,
    barShowPercent: true,
    barFillRow: false,
    textSize: '',
    headerAlign: '',
    emojiPreset: 'traffic',
    emojiRules: [],
    conditionalFormat: [],
    width: '',
    widthUnit: '%',
  };
}

export const COLUMN_SIZE_MODES = [
  { value: 'fill', label: 'جا دادن در عرض ویجت', hint: 'ستون‌های بدون عرض دستی، فضای باقی‌مانده را به‌طور مساوی تقسیم می‌کنند.' },
  { value: 'content', label: 'اندازه بر اساس محتوا', hint: 'هر ستون به پهن‌ترین سلول خودش اندازه می‌گیرد و در صورت نیاز جدول افقی اسکرول می‌شود.' },
  { value: 'header', label: 'اندازه بر اساس عنوان', hint: 'عرض هر ستون از متن عنوان می‌آید؛ محتوای بلند کوتاه می‌شود.' },
  { value: 'manual', label: 'عرض دستی ستون‌ها', hint: 'برای هر ستون درصد یا پیکسل بگذارید. ستون خالی فضای باقی‌مانده را می‌گیرد.' },
];

export function resolveColumnSizeMode(tableConfig = {}) {
  const mode = String(tableConfig.columnSizeMode || '').trim();
  if (mode === 'fill' || mode === 'content' || mode === 'header' || mode === 'manual') {
    return mode;
  }
  return tableConfig.fitColumns === false ? 'content' : 'fill';
}

export function parseColumnWidth(col) {
  if (!col || col.width === '' || col.width == null) return null;
  const n = Number(col.width);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = col.widthUnit === 'px' ? 'px' : '%';
  return { value: unit === '%' ? Math.min(n, 100) : Math.round(n), unit };
}

function headerWidthCh(col) {
  const label = String(col?.label || col?.key || '').replace(/\s+/g, ' ').trim();
  const chars = [...label].length;
  return Math.max(chars + 2, 5);
}

function widthStyle(parsed) {
  if (!parsed) return {};
  const width = `${parsed.value}${parsed.unit}`;
  return parsed.unit === 'px' ? { width, minWidth: width } : { width };
}

/**
 * Table-level fit mode plus optional per-column % / px overrides.
 * Percent is preferred on a resizing dashboard; px pins codes / icons.
 */
export function resolveTableColumnLayout(cols = [], tableConfig = {}) {
  const mode = resolveColumnSizeMode(tableConfig);
  const list = Array.isArray(cols) ? cols : [];
  const parsed = list.map(parseColumnWidth);
  const hasPercent = parsed.some((w) => w?.unit === '%');
  const constrain = mode === 'fill' || (mode === 'manual' && (hasPercent || parsed.every((w) => !w)));
  const truncate = mode === 'fill' || mode === 'header' || mode === 'manual';

  const styles = list.map((col, i) => {
    if (parsed[i]) return widthStyle(parsed[i]);
    if (mode === 'header') {
      return { width: `${headerWidthCh(col)}ch`, whiteSpace: 'nowrap' };
    }
    return {};
  });

  const tableClass = [
    '!min-w-0',
    mode === 'content' ? '' : 'table-fixed',
    constrain ? '' : '!w-max',
  ].filter(Boolean).join(' ');

  return {
    mode,
    fit: constrain,
    truncate,
    nowrap: mode === 'content',
    tableClass,
    styles,
  };
}

const AMOUNT_KEY_RE = /(amount|paid|commission|allocated|rial|price|claims)$/i;
const PERCENT_KEY_RE = /(percent|pct|achievement|ratio)$/i;

export function suggestColumnFormat(key, isMetric = false) {
  const k = String(key || '');
  if (PERCENT_KEY_RE.test(k)) {
    return { format: 'percent', decimals: 2, useGrouping: false, currencySuffix: 'none' };
  }
  if (AMOUNT_KEY_RE.test(k)) {
    return { format: 'currency', decimals: 2, useGrouping: true, currencySuffix: 'rial' };
  }
  if (isMetric) {
    return { format: 'number', decimals: 2, useGrouping: true, currencySuffix: 'none' };
  }
  return { format: 'text', decimals: 2, useGrouping: false, currencySuffix: 'none' };
}

export function newFieldColumn(key, metrics = []) {
  const isMet = (metrics || []).includes(key);
  const suggested = suggestColumnFormat(key, isMet);
  return {
    ...defaultTableColumn(),
    key,
    label: colLabel(key),
    source: 'field',
    ...suggested,
  };
}

/**
 * Keep existing column settings; append query keys that are missing;
 * drop data columns that left the query; keep custom columns.
 */
export function syncTableColumnsFromQueryKeys(existing = [], keys = [], metrics = []) {
  const keyList = [...new Set((keys || []).filter(Boolean))];
  const prev = Array.isArray(existing) ? existing : [];
  if (!keyList.length) return prev;

  const keySet = new Set(keyList);
  const kept = prev
    .filter((c) => isCustomColumn(c) || (c?.key && keySet.has(c.key)))
    .map((c) => (isCustomColumn(c) ? c : withPersianLabel(c)));
  const have = new Set(kept.filter((c) => !isCustomColumn(c) && c?.key).map((c) => c.key));
  const added = keyList.filter((k) => !have.has(k)).map((k) => newFieldColumn(k, metrics));
  if (!added.length && kept.length === prev.length) return prev;

  let lastField = -1;
  kept.forEach((c, i) => {
    if (!isCustomColumn(c)) lastField = i;
  });
  if (lastField < 0) return [...added, ...kept];
  return [...kept.slice(0, lastField + 1), ...added, ...kept.slice(lastField + 1)];
}
